import React, {useContext, useEffect, useState} from "react";
import {BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid} from "recharts";
import axiosInstance from "../../AxiosInstance";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClipboard, faCog, faPencil, faTrash} from "@fortawesome/free-solid-svg-icons";
import Modal from "../modal/modal";
import {SelectInput, TextInput} from "../input/inputs";
import MyContext from "../../Context";
import './production_chart.css';

export default function ProductionChart({variables}) {
    const [productionData, setProductionData] = useState(null);
    const [period, setPeriod] = useState('7d');
    const [viewMode, setViewMode] = useState('day'); // 'day' | 'shift'
    const [shiftData, setShiftData] = useState(null);
    const [shifts, setShifts] = useState([]);
    const [showDashboardModal, setShowDashboardModal] = useState(false);
    const [showShiftsModal, setShowShiftsModal] = useState(false);
    const [shiftForm, setShiftForm] = useState({color: '#4F46E5', start_hour: 0, start_minute: 0, end_hour: 8, end_minute: 0});
    const [editingShift, setEditingShift] = useState(null); // null = create, id = edit
    const [form, setForm] = useState({});
    const {dashboards} = useContext(MyContext);

    const device = JSON.parse(localStorage.getItem('device'));
    const projectId = localStorage.getItem('project_id');

    // Find the first production counter variable (either good or bad) to use for fetching
    const productionVar = (variables || []).find(v => v.is_production_counter);

    useEffect(() => {
        if (productionVar && viewMode === 'day') {
            axiosInstance.get(`variable_data/${productionVar.id}/production_histogram/?period=${period}`)
                .then(res => setProductionData(res.data))
                .catch(() => setProductionData(null));
        }
    }, [productionVar?.id, period, viewMode]);

    useEffect(() => {
        if (productionVar && viewMode === 'shift') {
            axiosInstance.get(`variable_data/${productionVar.id}/shift_comparison/?period=${period}`)
                .then(res => setShiftData(res.data))
                .catch(() => setShiftData(null));
        }
    }, [productionVar?.id, period, viewMode]);

    useEffect(() => {
        if (device?.id) {
            axiosInstance.get(`production_shift/?device_id=${device.id}`).then(res => setShifts(Array.isArray(res.data) ? res.data : (res.data?.results || [])));
        }
    }, [device?.id]);

    if (!productionVar) return null;

    const samplingOptions = [
        {id: 0, name: '7 Days'},
        {id: 1, name: '30 Days'},
        {id: 2, name: 'Monthly'}
    ];

    const addToDashboard = () => {
        const payload = {
            name: form.widget_name,
            key: productionVar.id,
            type: 6,
        };
        if (form.widget_sampling !== null && form.widget_sampling !== undefined && form.widget_sampling !== '') {
            payload.widget_sampling = parseInt(form.widget_sampling);
        }
        axiosInstance.post(`dashboard/${form.dashboard_id}/add_widget/`, payload).then(() => {
            window.location.reload();
        });
    };

    const EMPTY_SHIFT_FORM = {name: '', color: '#4F46E5', start_hour: 0, start_minute: 0, end_hour: 8, end_minute: 0};

    const shiftPayload = () => ({
        ...shiftForm,
        device: device?.id,
        project: parseInt(projectId),
        start_hour: parseInt(shiftForm.start_hour),
        start_minute: parseInt(shiftForm.start_minute || 0),
        end_hour: parseInt(shiftForm.end_hour),
        end_minute: parseInt(shiftForm.end_minute || 0),
    });

    const createShift = () => {
        axiosInstance.post('production_shift/', shiftPayload()).then(res => {
            setShifts(prev => [...prev, res.data]);
            setShiftForm(EMPTY_SHIFT_FORM);
        });
    };

    const saveShiftEdit = () => {
        axiosInstance.patch(`production_shift/${editingShift}/`, shiftPayload()).then(res => {
            setShifts(prev => prev.map(s => s.id === editingShift ? res.data : s));
            setEditingShift(null);
            setShiftForm(EMPTY_SHIFT_FORM);
        });
    };

    const openEditShift = (s) => {
        setEditingShift(s.id);
        setShiftForm({name: s.name, color: s.color, start_hour: s.start_hour, start_minute: s.start_minute, end_hour: s.end_hour, end_minute: s.end_minute});
    };

    const cancelEditShift = () => {
        setEditingShift(null);
        setShiftForm(EMPTY_SHIFT_FORM);
    };

    const deleteShift = (id) => {
        axiosInstance.delete(`production_shift/${id}/`).then(() => {
            setShifts(prev => prev.filter(s => s.id !== id));
        });
    };

    const chartData = (() => {
        if (!productionData) return [];
        const mainData = productionData.data || [];
        const pairedData = productionData.paired_data || [];
        const mainRole = productionData.role || 'good';
        const pairedRole = productionData.paired_role;

        if (!pairedData.length) {
            return mainData.map(d => ({...d, [mainRole]: d.value}));
        }

        const dateMap = {};
        mainData.forEach(d => {
            dateMap[d.date] = {...d, [mainRole]: d.value};
        });
        pairedData.forEach(d => {
            if (dateMap[d.date]) {
                dateMap[d.date][pairedRole] = d.value;
            } else {
                dateMap[d.date] = {date: d.date, day_label: d.day_label, [pairedRole]: d.value};
            }
        });
        return Object.values(dateMap).sort((a, b) => a.date.localeCompare(b.date));
    })();

    const shiftChartData = (() => {
        if (!shiftData?.shifts?.length) return [];
        const dateSet = new Set();
        shiftData.shifts.forEach(s => s.data.forEach(d => dateSet.add(d.date)));
        const dates = Array.from(dateSet).sort();
        return dates.map(date => {
            const point = {date, day_label: shiftData.shifts[0]?.data.find(d => d.date === date)?.day_label || date};
            shiftData.shifts.forEach(s => {
                const d = s.data.find(x => x.date === date);
                point[s.name] = d ? d.value : 0;
            });
            return point;
        });
    })();

    const periodLabels = ['7d', '30d'].filter(p => p);
    const shiftPeriods = [{id: '7d', label: '7 Days'}, {id: '30d', label: '30 Days'}];

    return (
        <div className="pc-container">
            <Modal show={showDashboardModal} setter={setShowDashboardModal} name={'Add to Dashboard'} onClick={addToDashboard} buttonName={'Create'}>
                <div className={'mb-3'}>
                    <SelectInput name={'Dashboard'} internalName={'dashboard_id'} form={form} setForm={setForm} options={dashboards}/>
                    <SelectInput name={'Sampling'} internalName={'widget_sampling'} form={form} setForm={setForm} options={samplingOptions}/>
                    <TextInput name={'Name'} internalName={'widget_name'} form={form} setForm={setForm}/>
                </div>
            </Modal>

            <Modal show={showShiftsModal} setter={setShowShiftsModal} name={'Manage Shifts'} buttonName={null}>
                <div style={{marginBottom: '12px'}}>
                    {shifts.map(s => (
                        <div key={s.id} style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            padding: '8px 10px', borderRadius: '6px',
                            backgroundColor: editingShift === s.id ? '#EEF2FF' : '#F8FAFC',
                            border: `1px solid ${editingShift === s.id ? '#C7D2FE' : '#E2E8F0'}`,
                            marginBottom: '6px'
                        }}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                <div style={{width: '12px', height: '12px', borderRadius: '3px', backgroundColor: s.color}}/>
                                <span style={{fontWeight: 500}}>{s.name}</span>
                                <span style={{fontSize: '12px', color: '#64748B'}}>{String(s.start_hour).padStart(2,'0')}:{String(s.start_minute).padStart(2,'0')} – {String(s.end_hour).padStart(2,'0')}:{String(s.end_minute).padStart(2,'0')}</span>
                            </div>
                            <div style={{display: 'flex', gap: '10px', alignItems: 'center'}}>
                                <FontAwesomeIcon icon={faPencil} style={{cursor:'pointer', fontSize:'12px', color:'#94A3B8'}} onClick={() => openEditShift(s)}/>
                                <FontAwesomeIcon icon={faTrash} style={{cursor:'pointer', fontSize:'12px', color:'#EF4444'}} onClick={() => deleteShift(s.id)}/>
                            </div>
                        </div>
                    ))}
                </div>
                <div style={{borderTop: '1px solid #E2E8F0', paddingTop: '12px'}}>
                    <div style={{fontWeight: 600, fontSize: '13px', marginBottom: '8px'}}>
                        {editingShift ? 'Edit Shift' : 'Add Shift'}
                    </div>
                    <TextInput name={'Shift Name'} internalName={'name'} form={shiftForm} setForm={setShiftForm}/>
                    <div style={{display: 'flex', gap: '8px', marginBottom: '8px'}}>
                        <div style={{flex: 1}}>
                            <label style={{fontSize: '12px', color: '#64748B'}}>Start Hour (0-23)</label>
                            <input type="number" min="0" max="23" value={shiftForm.start_hour}
                                onChange={e => setShiftForm(f => ({...f, start_hour: e.target.value}))}
                                style={{width:'100%', padding:'6px 8px', border:'1px solid #CBD5E1', borderRadius:'4px', fontSize:'13px'}}/>
                        </div>
                        <div style={{flex: 1}}>
                            <label style={{fontSize: '12px', color: '#64748B'}}>End Hour (0-23)</label>
                            <input type="number" min="0" max="23" value={shiftForm.end_hour}
                                onChange={e => setShiftForm(f => ({...f, end_hour: e.target.value}))}
                                style={{width:'100%', padding:'6px 8px', border:'1px solid #CBD5E1', borderRadius:'4px', fontSize:'13px'}}/>
                        </div>
                        <div style={{flex: 1}}>
                            <label style={{fontSize: '12px', color: '#64748B'}}>Color</label>
                            <input type="color" value={shiftForm.color}
                                onChange={e => setShiftForm(f => ({...f, color: e.target.value}))}
                                style={{width:'100%', height:'32px', padding:'2px', border:'1px solid #CBD5E1', borderRadius:'4px', cursor:'pointer'}}/>
                        </div>
                    </div>
                    <div style={{display: 'flex', gap: '8px'}}>
                        <button onClick={editingShift ? saveShiftEdit : createShift} style={{
                            padding:'6px 16px', borderRadius:'6px', border:'none',
                            backgroundColor:'#4F46E5', color:'#fff', cursor:'pointer', fontSize:'13px', fontWeight:600
                        }}>{editingShift ? 'Save' : 'Add'}</button>
                        {editingShift && (
                            <button onClick={cancelEditShift} style={{
                                padding:'6px 16px', borderRadius:'6px', border:'1px solid #E2E8F0',
                                backgroundColor:'#F8FAFC', color:'#64748B', cursor:'pointer', fontSize:'13px'
                            }}>Cancel</button>
                        )}
                    </div>
                </div>
            </Modal>

            <div className="pc-header">
                <div className="pc-left">
                    <div style={{fontSize: '15px', fontWeight: 600, color: '#334155'}}>Production</div>
                    <FontAwesomeIcon
                        role={'button'}
                        icon={faClipboard}
                        onClick={() => {
                            const deviceName = device?.name || '';
                            const defaultName = deviceName ? `Production - ${deviceName}` : 'Production';
                            setForm({widget_name: defaultName, widget_sampling: ''});
                            setShowDashboardModal(true);
                        }}
                        color={'#94a3b8'}
                        style={{cursor: 'pointer', fontSize: '13px'}}
                    />
                    {/* View mode toggle */}
                    <div style={{display: 'flex', borderRadius: '6px', border: '1px solid #E2E8F0', overflow: 'hidden'}}>
                        {[{id:'day', label:'By Day'}, {id:'shift', label:'By Shift'}].map(m => (
                            <button key={m.id} onClick={() => setViewMode(m.id)} style={{
                                padding: '3px 10px', border: 'none', fontSize: '11px', fontWeight: 500,
                                cursor: 'pointer',
                                backgroundColor: viewMode === m.id ? '#4F46E5' : '#F8FAFC',
                                color: viewMode === m.id ? '#fff' : '#64748B',
                            }}>{m.label}</button>
                        ))}
                    </div>
                    {viewMode === 'shift' && (
                        <FontAwesomeIcon
                            icon={faCog}
                            color={'#94a3b8'}
                            style={{cursor: 'pointer', fontSize: '13px'}}
                            onClick={() => setShowShiftsModal(true)}
                            title={'Manage Shifts'}
                        />
                    )}
                </div>
                <div className="pc-periods">
                    {viewMode === 'day'
                        ? ['7d', '30d', 'monthly', 'yearly'].map(p => (
                            <span key={p} onClick={() => setPeriod(p)} style={{
                                cursor: 'pointer', fontWeight: period === p ? 700 : 400, fontSize: '12px',
                                color: period === p ? '#4F46E5' : '#94A3B8', padding: '4px 8px', borderRadius: '6px',
                                backgroundColor: period === p ? '#EEF2FF' : 'transparent'
                            }}>
                                {p === '7d' ? '7 Days' : p === '30d' ? '30 Days' : p === 'monthly' ? 'Monthly' : 'Yearly'}
                            </span>
                        ))
                        : shiftPeriods.map(p => (
                            <span key={p.id} onClick={() => setPeriod(p.id)} style={{
                                cursor: 'pointer', fontWeight: period === p.id ? 700 : 400, fontSize: '12px',
                                color: period === p.id ? '#4F46E5' : '#94A3B8', padding: '4px 8px', borderRadius: '6px',
                                backgroundColor: period === p.id ? '#EEF2FF' : 'transparent'
                            }}>{p.label}</span>
                        ))
                    }
                </div>
            </div>

            {viewMode === 'day' ? (
                chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={247}>
                        <BarChart data={chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                            <XAxis dataKey="day_label" tick={{fontSize: 11}}/>
                            <YAxis tick={{fontSize: 11}}/>
                            <Tooltip/>
                            <Legend/>
                            <Bar dataKey="good" fill="#22c55e" radius={[4, 4, 0, 0]} name="Good"/>
                            <Bar dataKey="bad" fill="#ef4444" radius={[4, 4, 0, 0]} name="Bad"/>
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div style={{padding: '40px', textAlign: 'center', color: '#94A3B8', fontSize: '13px', backgroundColor: '#F8FAFC', borderRadius: '8px', height: '247px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        No production data yet. Data will appear after the daily calculation runs.
                    </div>
                )
            ) : (
                shifts.length === 0 ? (
                    <div style={{padding: '40px', textAlign: 'center', color: '#94A3B8', fontSize: '13px', backgroundColor: '#F8FAFC', borderRadius: '8px', height: '247px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        No shifts defined. Click the gear icon to create shifts.
                    </div>
                ) : shiftChartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={247}>
                        <BarChart data={shiftChartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9"/>
                            <XAxis dataKey="day_label" tick={{fontSize: 11}}/>
                            <YAxis tick={{fontSize: 11}}/>
                            <Tooltip/>
                            <Legend/>
                            {(shiftData?.shifts || []).map(s => (
                                <Bar key={s.name} dataKey={s.name} fill={s.color} radius={[4, 4, 0, 0]}/>
                            ))}
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <div style={{padding: '40px', textAlign: 'center', color: '#94A3B8', fontSize: '13px', backgroundColor: '#F8FAFC', borderRadius: '8px', height: '247px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        No shift data for this period.
                    </div>
                )
            )}
        </div>
    );
}
